import React, { useCallback, useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { index as searchApi } from '@afosto/content/api/Index';
import Autocomplete, { highlightSubstring } from '../Autocomplete';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Avatar from '@material-ui/core/Avatar';
import Typography from '@material-ui/core/Typography';
import translations from './translations';
import resizeImageUrl from '@afosto/utils/resizeImageUrl';

const ProductSearch = ({ autoFocus, inputProps, ...props }) => {
  const intl = useIntl();
  const [isFetchingProducts, setIsFetchingProducts] = useState(false);
  const [productSuggestions, setProductSuggestions] = useState([]);

  /**
   * Search for products
   * @param {string} searchValue
   * @returns {Promise}
   */
  const searchProducts = useCallback(async searchValue => {
    try {
      setIsFetchingProducts(true);

      const response = await searchApi.search(
        {
          codes: ['pim'],
          models: ['products'],
          namespaces: ['default'],
          q: searchValue?.length > 0 ? [searchValue] : null,
        },
        {
          headers: {
            'x-page-size': 100,
          },
        },
      );
      const { data = {} } = response;
      const { hits = [] } = data;
      const suggestions = hits.reduce((acc, hit) => {
        const { name, sku, id, image, ean } = hit.metadata || {};
        return [
          ...acc,
          {
            id,
            name: `${name}`,
            sku: `${sku}`,
            label: `${name}`,
            image: `${image}`,
            ean: `${ean}`,
            value: id,
          },
        ];
      }, []);

      setProductSuggestions(suggestions);
    } catch (err) {
      setProductSuggestions([]);
    } finally {
      setIsFetchingProducts(false);
    }
  }, []);

  useEffect(() => {
    searchProducts();
  }, [searchProducts]);

  /**
   * Search products based on the input value.
   * @param {Event} event
   */
  const handleSearchProduct = event => {
    const { value } = event?.target ?? {};

    searchProducts(value).catch(() => {
      // Do something with error.
    });
  };

  /**
   * Render the product options in the auto complete dropdown.
   * @param {object} option
   * @param {string} inputValue
   * @returns {Component}
   */
  const renderProductOptions = (option, { inputValue = '' } = {}) => {
    let label = option;

    if (typeof option === 'object') {
      // eslint-disable-next-line prefer-destructuring
      label = option.label;
    }

    return (
      <ListItem component="div" dense disableGutters>
        <ListItemAvatar>
          <Avatar alt={label} src={resizeImageUrl(option.image, 50)} />
        </ListItemAvatar>
        <ListItemText>
          <Typography variant="subtitle1">
            {highlightSubstring(option.label, inputValue)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {highlightSubstring(option.sku, inputValue)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {highlightSubstring(option.ean, inputValue)}
          </Typography>
        </ListItemText>
      </ListItem>
    );
  };

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: option => `${option.label} ${option.ean ?? ''} ${option.sku ?? ''}`,
  });

  return (
    <Autocomplete
      {...props}
      loading={isFetchingProducts}
      options={productSuggestions}
      filterOptions={filterOptions}
      onInputChange={handleSearchProduct}
      autocomplete="off"
      autoFocus={autoFocus}
      renderOption={renderProductOptions}
      inputProps={{
        ...(inputProps ?? {}),
        placeholder: intl.formatMessage(translations.fields.product.placeholder),
      }}
    />
  );
};

ProductSearch.propTypes = Autocomplete.propTypes;

ProductSearch.defaultProps = {
  ...Autocomplete.defaultProps,
  autoFocus: true,
};

export default ProductSearch;
